import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { User } from 'firebase/auth';
import { signInWithGoogle, signOut, isEmailAllowed, getDisplayNameForUser } from '../services/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { hasAppAccess, AVAILABLE_APPS } from '../services/appAccess';
import { userActivityService } from '../services/userActivityService';
import { getUserProfile } from '../services/userProfileService';

interface AuthContextType {
  user: User | null;
  userEmail: string | null;
  displayName: string | null;
  profilePictureUrl: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: () => Promise<void>;
  logout: () => Promise<void>;
  checkAppAccess: (appId: string) => Promise<boolean>;
  refreshProfilePicture: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  userEmail: null,
  displayName: null,
  profilePictureUrl: null,
  isLoading: true,
  isAuthenticated: false,
  signIn: async () => {},
  logout: async () => {},
  checkAppAccess: async () => false,
  refreshProfilePicture: async () => {}
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Track the last recorded app access to prevent duplicate calls
  const lastRecordedRef = useRef<{
    userId: string;
    appId: string;
    timestamp: number;
  } | null>(null);

  const checkAppAccess = useCallback(async (appId: string) => {
    if (!userEmail) return false;
    return await hasAppAccess(userEmail, appId);
  }, [userEmail]);

  // Helper to update email state and storage
  const updateUserEmail = useCallback((email: string | null) => {
    setUserEmail(email);
    if (email) {
      localStorage.setItem('userEmail', email);
    } else {
      localStorage.removeItem('userEmail');
    }
  }, []);

  // Helper function to record app access with debounce
  const recordAppAccess = useCallback(async (userId: string, email: string, appId: string) => {
    const now = Date.now();
    const lastRecorded = lastRecordedRef.current;
    
    // Debounce: only record if it's been more than 1 hour since the last recording for this user+app
    // or if it's a different app or user
    if (
      !lastRecorded || 
      lastRecorded.userId !== userId || 
      lastRecorded.appId !== appId || 
      now - lastRecorded.timestamp > 60 * 60 * 1000 // 1 hour debounce (increased from 10 seconds)
    ) {
      lastRecordedRef.current = {
        userId,
        appId,
        timestamp: now
      };
      
      try {
        // Use the firestoreWrapper to apply rate limiting
        const { firestoreWrapper } = await import('../services/firestoreWrapper');
        const { rateLimiter } = await import('../services/rateLimiter');
        
        // Check if we're approaching the rate limit
        const status = rateLimiter.getStatus();
        if (status.currentCount > status.limit * 0.7) {
          return;
        }
        
        await userActivityService.recordDailyAppAccess(userId, email, appId);
      } catch (error) {
        // Silently fail - don't block user navigation due to tracking errors
      }
    }
  }, []);

  // Check if user has access to current app and record app access
  const checkCurrentAppAccess = useCallback(async (email: string, currentPath: string) => {
    const path = currentPath;
    let appId = '';

    // Determine which app the user is accessing based on the path
    if (path.includes('good-things')) {
      appId = AVAILABLE_APPS.GoodThingTracker;
    }
    else if (path.includes('plms')) {
      appId = AVAILABLE_APPS.PLMS;
    }
    else if (path.includes('sop')) {
      appId = AVAILABLE_APPS.SOPCentral;
    }
    else if (path.includes('dayaway')) {
      appId = AVAILABLE_APPS.DayAwayBoard;
    }
    else if (path.includes('ideogram')) {
      appId = AVAILABLE_APPS.IdeogramPromptStorm;
    }
    else if (path.includes('inventory')) {
      appId = AVAILABLE_APPS.ListingManagementSystem;
    }
    else if (path.includes('idea-machine')) {
      appId = AVAILABLE_APPS.IdeaMachine;
    }
    else if (path.includes('glossary')) {
      appId = AVAILABLE_APPS.Glossary;
    }
    else if (path.includes('product-manager')) {
      appId = AVAILABLE_APPS.ProductManager;
    }
    else if (path.includes('ppc-farm')) {
      appId = AVAILABLE_APPS.PPCFarmMailTracker;
    }
    else if (path.includes('sb-search-term-report')) {
      appId = AVAILABLE_APPS.SBSearchTermReport;
    }
    else if (path.includes('amazon-index-checker')) {
      appId = AVAILABLE_APPS.AmazonIndexChecker;
    }
    else if (path.includes('primary-ledger')) {
      appId = AVAILABLE_APPS.PrimaryLedger;
    }
    else if (path.includes('asin-sales-history')) {
      appId = AVAILABLE_APPS.ASINSalesHistory;
    }
    else if (path.includes('days-of-cover')) {
      appId = AVAILABLE_APPS.DaysOfCover;
    }
    else if (path.includes('sales-and-profit-history')) {
      appId = AVAILABLE_APPS.ASINSalesHistory;
    }
    else if (path.includes('smart-writer')) {
      appId = 'smart-writer';
      // Smart Writer access is always allowed (like Apps Hub)
      if (user && user.uid) {
        await recordAppAccess(user.uid, email, appId);
      }
      return true;
    }
    else if (path === '/' || path === '/dashboard') {
      appId = 'hub';
      // Hub access is always allowed
      if (user && user.uid) {
        await recordAppAccess(user.uid, email, appId);
      }
      return true;
    }
    else {
      // Non-app pages are always allowed
      return true;
    }

    // Special case for admin paths - always allow if user is admin
    if (path.includes('/admin')) {
      // Still record the access for the parent app
      if (user && user.uid && appId && !path.includes('login')) {
        await recordAppAccess(user.uid, email, appId);
      }
      
      // Admin access is checked by AdminProtectedRoute, so we just return true here
      return true;
    }
    
    // Record app access if user is authenticated and we have an appId
    // Only do this when the user is actually accessing an app page, not during auth checks
    if (user && user.uid && appId && !path.includes('login')) {
      await recordAppAccess(user.uid, email, appId);
    }

    // Check if user has access to the app
    return appId ? await hasAppAccess(email, appId) : true;
  }, [user, recordAppAccess]);

  // Set up cache invalidation listener
  useEffect(() => {
    let cleanup: (() => void) | undefined;
    
    const setupListener = async () => {
      try {
        const { setupCacheInvalidationListener } = await import('../services/cacheInvalidation');
        cleanup = setupCacheInvalidationListener();
      } catch (error) {
        // Silently handle cache invalidation setup errors
      }
    };
    
    setupListener();
    
    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, []);

  useEffect(() => {
    // Initialize email from localStorage if available
    const storedEmail = localStorage.getItem('userEmail');
    if (storedEmail) {
      setUserEmail(storedEmail);
    }

    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setIsLoading(true);
      try {
        if (firebaseUser && firebaseUser.email) {
          // Check if the user's email is allowed and has access to current app
          const [allowed, hasAccess] = await Promise.all([
            isEmailAllowed(firebaseUser.email).then(async allowed => {
              const displayName = await getDisplayNameForUser(firebaseUser.email || '');
              setDisplayName(displayName);
              return allowed;
            }),
            checkCurrentAppAccess(firebaseUser.email, window.location.pathname)
          ]);

          if (allowed) {
            setUser(firebaseUser);
            setUserEmail(firebaseUser.email);
            setIsAuthenticated(true);
            
            // We don't need to record hub access here as it will be handled by checkCurrentAppAccess
            // when the user navigates to the dashboard
            
            if (!hasAccess) {
              // If user doesn't have access to current app, redirect to dashboard
              navigate('/dashboard', { replace: true });
            } else if (window.location.pathname === '/' || window.location.pathname === '/login') {
              // If we're on the login page, redirect to dashboard or saved URL
              const redirectUrl = localStorage.getItem('redirectUrl') || '/dashboard';
              localStorage.removeItem('redirectUrl');
              navigate(redirectUrl, { replace: true });
            }
          } else {
            // If email is not allowed, sign out
            await signOut();
            setUser(null);
            updateUserEmail(null);
            setIsAuthenticated(false);
            navigate('/', { replace: true });
          }
        } else {
          // No user is signed in
          setUser(null);
          updateUserEmail(null);
          setIsAuthenticated(false);
          
          // If we're not on the login page or a legal page, redirect to login
          const publicPages = ['/', '/login', '/privacy-policy', '/cookie-policy', '/terms-of-service'];
          if (!publicPages.includes(window.location.pathname)) {
            localStorage.setItem('redirectUrl', window.location.pathname);
            navigate('/', { replace: true });
          }
        }
      } catch (error) {
        setUser(null);
        updateUserEmail(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    });

    // Cleanup subscription
    return () => unsubscribe();
  }, []); // Only run once on mount

  // Check app access on route change without triggering loading state
  useEffect(() => {
    const checkAccess = async () => {
      if (user && user.email && isAuthenticated) {
        const hasAccess = await checkCurrentAppAccess(user.email, location.pathname);
        if (!hasAccess) {
          navigate('/dashboard', { replace: true });
        }
      }
    };
    checkAccess();
  }, [location.pathname, user, isAuthenticated, checkCurrentAppAccess, navigate]);

  const signIn = useCallback(async () => {
    try {
      setIsLoading(true);
      await signInWithGoogle();
      // Auth state listener will handle the rest
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Function to load the user's profile picture
  const refreshProfilePicture = useCallback(async () => {
    if (!user || !user.uid) {
      return;
    }
    
    try {
      // Add a small delay to ensure Firestore has propagated the changes
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const userProfile = await getUserProfile(user.uid);
      
      if (userProfile) {
        // Check both profilePicture (new field) and photoURL (backward compatibility)
        const pictureUrl = userProfile.profilePicture || userProfile.photoURL || null;
        setProfilePictureUrl(pictureUrl);
        setDisplayName(userProfile.displayName || null);
        
        // If no profile picture found, clear the cache
        if (!pictureUrl && user.email) {
          localStorage.removeItem(`profilePicture_${user.email}`);
        }
      } else {
        setProfilePictureUrl(null);
        // Clear cache when no user profile exists
        if (user.email) {
          localStorage.removeItem(`profilePicture_${user.email}`);
        }
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      setProfilePictureUrl(null);
      // Clear cache on error
      if (user.email) {
        localStorage.removeItem(`profilePicture_${user.email}`);
      }
    }
  }, [user]);

  // Load profile picture when user changes
  useEffect(() => {
    if (user) {
      refreshProfilePicture();
    } else {
      setProfilePictureUrl(null);
    }
  }, [user, refreshProfilePicture]);

  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      await signOut();
      setUser(null);
      updateUserEmail(null);
      setProfilePictureUrl(null);
      setDisplayName(null);
      setIsAuthenticated(false);
      navigate('/', { replace: true });
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [navigate, updateUserEmail]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      userEmail, 
      displayName,
      profilePictureUrl,
      isLoading, 
      isAuthenticated,
      signIn,
      logout,
      checkAppAccess,
      refreshProfilePicture
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
